import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Flex, Icon, theme } from '@aloft/toolkit-sui';

import { Control, useWatch } from 'react-hook-form';

import { selectSalesComparisonApproach } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getColorForStatusOrSubject } from '../../../utils/getColorForStatusOrSubject.ts';
import { CraftsmanPropertyHeader } from '../../CraftsmanPropertyHeader/CraftsmanPropertyHeader.tsx';
import { ValuationTablePropertyHeaderColorBand } from '../../CraftsmanPropertyHeader/internal/ValuationTablePropertyHeaderColorBand.tsx';
import { PropertyGridForm, PropertyGridType } from '../types.ts';
import { getDataSourceDisplayInfo } from '../utils.ts';

type HeaderPropertyGridSectionProps = {
  columnIndex: number;
  gridType: PropertyGridType;
  control: Control<PropertyGridForm>;
};

export const HeaderPropertyGridSection = ({
  columnIndex,
  gridType,
  control,
}: HeaderPropertyGridSectionProps) => {
  const columnData = useWatch({ control, name: `columns.${columnIndex}` });
  const salesComparisonApproach = useAppSelector(selectSalesComparisonApproach);

  const { propertyData, type, propertyId } = columnData ?? {};

  const transaction = propertyData?.editableData?.transaction;

  if (!propertyData || !transaction) return null;

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const propertyStatus = isSubject ? 'Subject' : (transaction.status ?? 'Off-Market');
  const color = getColorForStatusOrSubject(propertyStatus);

  const isFeatureGrid = gridType === 'FeatureGrid';
  const showDataSourceDrawerToggler =
    isFeatureGrid || (gridType === 'AdjustmentsGrid' && salesComparisonApproach === 'unit_rate');

  if (type === 'property') {
    return (
      <Flex
        flexDirection="column"
        position="sticky"
        top="0"
        zIndex={theme.zIndices.sticky}
        __dangerDoNotUseSx={{ borderTop: `1px solid ${theme.colors.strokeDefault}` }}
      >
        <CraftsmanPropertyHeader
          propertyId={propertyId}
          gridType={gridType}
          showDataSourceDrawerToggler={showDataSourceDrawerToggler}
        />
      </Flex>
    );
  }

  const { icon, text } = getDataSourceDisplayInfo(type, propertyData);

  return (
    <Flex
      flexDirection="column"
      position="sticky"
      top="0"
      zIndex={theme.zIndices.sticky}
      alignItems="center"
      __dangerDoNotUseSx={{
        borderTop: `1px solid ${theme.colors.strokeDefault}`,
        color: theme.colors.buttonDefault,
      }}
    >
      <Flex flexDirection="column" width="100%" height="115px" backgroundColor="surface1">
        <Flex flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
          <Icon size="xl" icon={icon} color="buttonDefault" />
          {text}
        </Flex>
        <ValuationTablePropertyHeaderColorBand color={color} />
      </Flex>
    </Flex>
  );
};
